import type { LiveTranscriptionSession } from '@/shared/api/transcriptor';

import { LIVE_AUDIO_QUEUE_LIMIT } from '../config';
import { encodePcm16LittleEndian, LIVE_PCM_SAMPLE_RATE } from '../services';

import type {
  RealtimeTranscriptionConnection,
  RealtimeTranscriptionHandlers,
} from './realtimeTranscription';

// Deepgram live transcription over a browser WebSocket. Everything specific
// to the provider stays in this module; the rest of the live mode sees
// LiveTranscriptionEvent only.
//
// Scribe puts every stream parameter in the connect URL (model, language, raw
// linear16 PCM at the capture rate, interim results, smart formatting,
// endpointing) and grants a short-lived token, which a browser can only send
// as the `bearer` subprotocol. Audio goes as binary frames; `KeepAlive`,
// `Finalize` and `CloseStream` as text frames. `Results` messages carry
// stream times (`start`, `duration`, word `start`/`end`) and `is_final`.
//
// Deepgram transcribes the stream continuously and knows nothing of the
// client's phrases, so they are matched by stream time: a commit or a clear
// ends the phrase at the current stream time and sends `Finalize`, and final
// words belong to the phrase their time falls in. A phrase is complete once
// Deepgram has finalized the stream past its end (every later result starts
// where the last final one ended), or SETTLE_TIMEOUT_MS after it ended,
// because `Finalize` gets no answer when little audio is pending. Completed
// phrases are reported as `committed` + `completed`, in commit order, like
// OpenAI items; cleared ones are dropped, as OpenAI never transcribes cleared
// audio.

// Deepgram closes a socket that receives nothing for 10 s and asks for a
// KeepAlive every 3-5 s while no audio flows, e.g. while recording is paused.
const KEEP_ALIVE_INTERVAL_MS = 4000;
const SETTLE_TIMEOUT_MS = 2000;
// Absorbs the rounding between the client's stream clock and Deepgram's
// result times.
const SETTLE_TOLERANCE_SECONDS = 0.1;

type DeepgramWord = { start: number; end: number; text: string };

type DeepgramResult = {
  start: number;
  end: number;
  isFinal: boolean;
  transcript: string;
  words: DeepgramWord[];
};

export type ParsedDeepgramMessage =
  | { type: 'result'; result: DeepgramResult }
  | { type: 'error'; message: string }
  | null;

type ProviderWord = {
  word?: unknown;
  punctuated_word?: unknown;
  start?: unknown;
  end?: unknown;
};

type ProviderMessage = {
  type?: unknown;
  start?: unknown;
  duration?: unknown;
  is_final?: unknown;
  channel?: {
    alternatives?: { transcript?: unknown; words?: unknown }[];
  };
  description?: unknown;
  message?: unknown;
};

const readProviderMessage = (data: unknown): ProviderMessage | null => {
  if (typeof data !== 'string') {
    return null;
  }
  try {
    return JSON.parse(data) as ProviderMessage;
  } catch {
    return null;
  }
};

const isText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

const toSeconds = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const readWord = (word: ProviderWord): DeepgramWord[] => {
  const start = toSeconds(word.start);
  const end = toSeconds(word.end);
  const text = [word.punctuated_word, word.word].find(isText);
  return start === null || end === null || !text
    ? []
    : [{ start, end, text: text.trim() }];
};

export const parseDeepgramMessage = (data: unknown): ParsedDeepgramMessage => {
  const message = readProviderMessage(data);
  if (message?.type === 'Error') {
    return {
      type: 'error',
      message: [message.description, message.message].find(isText) ?? '',
    };
  }
  const start = toSeconds(message?.start);
  const duration = toSeconds(message?.duration);
  if (message?.type !== 'Results' || start === null || duration === null) {
    return null;
  }
  const alternative = message.channel?.alternatives?.[0];
  const transcript = alternative?.transcript;
  const words = alternative?.words;
  return {
    type: 'result',
    result: {
      start,
      end: start + duration,
      isFinal: message.is_final === true,
      transcript: isText(transcript) ? transcript.trim() : '',
      words: Array.isArray(words)
        ? (words as ProviderWord[]).flatMap(readWord)
        : [],
    },
  };
};

type DeepgramTurn = {
  itemId: string;
  // Stream seconds; infinite while the client has not ended the turn.
  end: number;
  kept: boolean;
  finals: string[];
  timer: number | null;
};

const wordTime = (word: DeepgramWord) => (word.start + word.end) / 2;

const joinWords = (words: DeepgramWord[]) =>
  words.map((word) => word.text).join(' ');

// A result without word timings is placed by its own window.
const resultWords = (result: DeepgramResult) =>
  result.words.length || !result.transcript
    ? result.words
    : [{ start: result.start, end: result.end, text: result.transcript }];

// The open turn ends at infinity, so every time has a turn.
const turnIndexAt = (turns: DeepgramTurn[], time: number) =>
  turns.findIndex((turn) => time <= turn.end);

const groupByTurn = (turns: DeepgramTurn[], words: DeepgramWord[]) =>
  words.reduce<Record<number, DeepgramWord[]>>((groups, word) => {
    const index = turnIndexAt(turns, wordTime(word));
    return { ...groups, [index]: [...(groups[index] ?? []), word] };
  }, {});

export const openDeepgramTranscription = ({
  session,
  handlers,
}: {
  session: LiveTranscriptionSession;
  handlers: RealtimeTranscriptionHandlers;
}): RealtimeTranscriptionConnection => {
  const socket = new WebSocket(session.connectUrl, [
    'bearer',
    session.clientSecret,
  ]);
  const connectionId = crypto.randomUUID();
  const queue: (string | ArrayBuffer)[] = [];
  const runtime = {
    turns: [] as DeepgramTurn[],
    turnCount: 0,
    streamSamples: 0,
    // Deepgram has finalized the stream up to here.
    finalizedThrough: 0,
    // Turns ending up to here waited SETTLE_TIMEOUT_MS.
    expiredThrough: 0,
    interim: [] as DeepgramWord[],
    shown: {} as Record<string, string>,
    audioSinceKeepAlive: false,
    keepAlive: null as number | null,
    closedByClient: false,
  };

  const openTurn = (): DeepgramTurn => {
    runtime.turnCount += 1;
    return {
      itemId: `${connectionId}-${runtime.turnCount}`,
      end: Number.POSITIVE_INFINITY,
      kept: true,
      finals: [],
      timer: null,
    };
  };
  runtime.turns = [openTurn()];

  const streamSeconds = () => runtime.streamSamples / LIVE_PCM_SAMPLE_RATE;

  const sendControl = (type: 'KeepAlive' | 'Finalize' | 'CloseStream') => {
    const payload = JSON.stringify({ type });
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(payload);
      return;
    }
    if (socket.readyState === WebSocket.CONNECTING && type === 'Finalize') {
      queue.push(payload);
    }
  };

  const stopTimers = () => {
    runtime.turns.forEach((turn) => {
      if (turn.timer !== null) {
        window.clearTimeout(turn.timer);
      }
    });
    if (runtime.keepAlive !== null) {
      window.clearInterval(runtime.keepAlive);
      runtime.keepAlive = null;
    }
  };

  const applyFinal = (result: DeepgramResult) => {
    const groups = groupByTurn(runtime.turns, resultWords(result));
    const single = Object.keys(groups).length === 1;
    runtime.turns = runtime.turns.map((turn, index) => {
      const words = groups[index];
      if (!words) {
        return turn;
      }
      const text =
        single && result.transcript ? result.transcript : joinWords(words);
      return { ...turn, finals: [...turn.finals, text] };
    });
    runtime.finalizedThrough = Math.max(runtime.finalizedThrough, result.end);
    runtime.interim = runtime.interim.filter(
      (word) => wordTime(word) > result.end,
    );
  };

  const applyInterim = (result: DeepgramResult) => {
    runtime.finalizedThrough = Math.max(runtime.finalizedThrough, result.start);
    runtime.interim = resultWords(result);
  };

  const report = (turn: DeepgramTurn) => {
    if (turn.timer !== null) {
      window.clearTimeout(turn.timer);
    }
    const shown = runtime.shown[turn.itemId];
    runtime.shown = Object.fromEntries(
      Object.entries(runtime.shown).filter(
        ([itemId]) => itemId !== turn.itemId,
      ),
    );
    if (turn.kept) {
      handlers.onEvent({ type: 'committed', itemId: turn.itemId });
      handlers.onEvent({
        type: 'completed',
        itemId: turn.itemId,
        text: turn.finals.join(' '),
      });
      return;
    }
    if (shown) {
      handlers.onEvent({ type: 'interim', itemId: turn.itemId, text: '' });
    }
  };

  const settle = () => {
    const watermark = Math.max(
      runtime.finalizedThrough + SETTLE_TOLERANCE_SECONDS,
      runtime.expiredThrough,
    );
    const pending = runtime.turns.findIndex((turn) => turn.end > watermark);
    const settled = runtime.turns.slice(0, pending);
    runtime.turns = runtime.turns.slice(pending);
    settled.forEach(report);
  };

  const showInterims = () => {
    const interimGroups = groupByTurn(runtime.turns, runtime.interim);
    runtime.turns.forEach((turn, index) => {
      const text = turn.kept
        ? [...turn.finals, joinWords(interimGroups[index] ?? [])]
            .filter(Boolean)
            .join(' ')
        : '';
      if (text !== (runtime.shown[turn.itemId] ?? '')) {
        runtime.shown = { ...runtime.shown, [turn.itemId]: text };
        handlers.onEvent({ type: 'interim', itemId: turn.itemId, text });
      }
    });
  };

  const refresh = () => {
    if (runtime.closedByClient) {
      return;
    }
    settle();
    showInterims();
  };

  // Events are emitted after the caller has recorded the turn it just ended.
  const endTurn = (kept: boolean) => {
    const end = streamSeconds();
    const timer = window.setTimeout(() => {
      runtime.expiredThrough = Math.max(runtime.expiredThrough, end);
      refresh();
    }, SETTLE_TIMEOUT_MS);
    runtime.turns = [
      ...runtime.turns.map((turn) =>
        turn.end === Number.POSITIVE_INFINITY
          ? { ...turn, end, kept, timer }
          : turn,
      ),
      openTurn(),
    ];
    sendControl('Finalize');
    queueMicrotask(refresh);
  };

  socket.addEventListener('open', () => {
    queue.splice(0).forEach((payload) => socket.send(payload));
    runtime.keepAlive = window.setInterval(() => {
      if (!runtime.audioSinceKeepAlive) {
        sendControl('KeepAlive');
      }
      runtime.audioSinceKeepAlive = false;
    }, KEEP_ALIVE_INTERVAL_MS);
    handlers.onOpen();
  });
  socket.addEventListener('message', (message: MessageEvent) => {
    const parsed = parseDeepgramMessage(message.data);
    if (!parsed) {
      return;
    }
    if (parsed.type === 'error') {
      handlers.onProviderError(parsed.message);
      return;
    }
    if (parsed.result.isFinal) {
      applyFinal(parsed.result);
    } else {
      applyInterim(parsed.result);
    }
    refresh();
  });
  socket.addEventListener('close', () => {
    stopTimers();
    if (!runtime.closedByClient) {
      handlers.onClose();
    }
  });

  return {
    // Audio captured while the socket is still opening is sent once it opens.
    // Audio beyond the queue limit is dropped before it counts towards the
    // stream clock, which has to stay equal to Deepgram's.
    appendAudio: (samples) => {
      const frame = encodePcm16LittleEndian(samples);
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(frame);
      } else if (
        socket.readyState === WebSocket.CONNECTING &&
        queue.length < LIVE_AUDIO_QUEUE_LIMIT
      ) {
        queue.push(frame);
      } else {
        return;
      }
      runtime.streamSamples += samples.length;
      runtime.audioSinceKeepAlive = true;
    },
    commit: () => endTurn(true),
    clear: () => endTurn(false),
    close: () => {
      runtime.closedByClient = true;
      stopTimers();
      queue.splice(0);
      sendControl('CloseStream');
      socket.close();
    },
  };
};
